/*
  <animated-image> — pause/play control for animated GIFs.

  Accessibility — WCAG 2.2.2 Pause, Stop, Hide / SortSite F7
  ("Avoid animated images over 5 seconds long that can't be paused or stopped",
  flagged on /pages/making-our-gummies for the process-step GIFs).

  A GIF cannot be paused through any media API, so "pause" is implemented by
  painting the frame that is currently on screen into a <canvas> and layering
  that canvas over the still-running <img>. Overlaying rather than replacing is
  deliberate: the <img> stays in the DOM and in the accessibility tree, so its
  alt text is unaffected, and resuming is a single node removal with no network
  request and no restart of the animation.

  The canvas is cross-origin-tainted (Shopify CDN), which is fine — drawImage
  and rendering are permitted; only pixel readback would be blocked, and we
  never read back.

  Users with `prefers-reduced-motion: reduce` get the paused state on load, and
  can still press play if they want the animation.
*/
(function () {
  if (window.customElements && customElements.get('animated-image')) return;

  class AnimatedImage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.image = this.querySelector('img');
      this.button = this.querySelector('.animated-image__toggle');
      this.labelTarget = this.querySelector('.animated-image__toggle-text');
      if (!this.image || !this.button) return;

      this.frame = null;

      // The control is authored hidden so that it never appears without the
      // script that makes it work.
      this.button.hidden = false;
      this.button.addEventListener('click', this.toggle.bind(this));

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.whenDecoded(this.pause.bind(this));
      }
    }

    whenDecoded(callback) {
      if (this.image.complete && this.image.naturalWidth) {
        callback();
      } else {
        this.image.addEventListener('load', callback, { once: true });
      }
    }

    get paused() {
      return this.frame !== null;
    }

    toggle() {
      if (this.paused) {
        this.play();
      } else {
        this.pause();
      }
    }

    pause() {
      if (this.paused) return;

      const width = this.image.naturalWidth;
      const height = this.image.naturalHeight;
      if (!width || !height) return;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.className = 'animated-image__frame';
      canvas.setAttribute('aria-hidden', 'true');

      try {
        canvas.getContext('2d').drawImage(this.image, 0, 0, width, height);
      } catch (error) {
        // Nothing was painted, so leaving the animation running is better than
        // covering it with an empty canvas.
        return;
      }

      this.appendChild(canvas);
      this.frame = canvas;
      this.setAttribute('data-paused', '');
      this.setLabel(this.button.dataset.playLabel);
    }

    play() {
      if (!this.paused) return;

      this.frame.remove();
      this.frame = null;
      this.removeAttribute('data-paused');
      this.setLabel(this.button.dataset.pauseLabel);
    }

    setLabel(text) {
      if (!text || !this.labelTarget) return;
      this.labelTarget.textContent = text;
    }
  }

  customElements.define('animated-image', AnimatedImage);
})();
